// src/pages/HomePage.tsx — Phase 1, rewired in Slice C commit 5.
//
// Home shell ported from docs/prototype/shell.jsx 263-292. Page-local
// components compose the page: HomeHero, AlertBanner, DutyCard, and
// NewsletterTeaser. (Quick Links grid removed 2026-05-29 to reclaim space.)
//
// Data flow: one round-trip to /api/dashboard (api.md §4.1) returns the
// banner text, today's DM/MOM/EM (with phones + hubUserId — see DutyCard),
// and the latest published newsletter in a single composite payload. No
// client-side fallbacks — if the fetch fails, show an error card with
// a single message.
//
// Duty card on Home: phone number is rendered as readable text under the
// officer's name, with a tap-to-call button AND a Quick Message button
// to the right of the row. canViewMobile rule #5 ("today's roster
// bypass") grants phone visibility for today's rostered officers to
// every authenticated viewer; the dashboard handler emits c.phone and
// the linked hub_users.id, and this card surfaces both. The wider
// /contacts directory remains strictly gated (filterContactForViewer
// per row).
//
// Phase 9 follow-up (2026-05-21) — HeadlinesSection is gated on
// `me.apps.headlines !== false`. Using `!== false` rather than `=== true`
// is defensive: if the /api/me response is briefly stale (e.g. just after
// a deploy that adds the key) the section still renders rather than being
// hidden on undefined.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser, type MeResponse } from "../auth";
import { ReportsHomeSlice } from "../components/reports/ReportsHomeSlice";
import { C, F } from "../theme";
import {
  Avatar,
  Button,
  Card,
  DoubleRule,
  Eyebrow,
  Icons,
  Section,
  Stamp,
} from "../components/ui";
import { HeadlinesSection } from "./HeadlinesSection";
import {
  ComposeSheet,
  ContactQmButton,
  QuickMessageHomeSection,
} from "../components/quickMessage";
import { DutySurface } from "../components/duty/DutySurface";
import { GroupQmSurface } from "../components/groupqm/GroupQmSurface";
import { ActiveCalloutCards } from "../components/firstAider/ActiveCalloutCards";
import { EventsLiveBanner } from "../eventslive/EventsLiveBanner";

// Phase 7 -- the bottom-nav dot reads `me.inbox.quickMessages.unread` from
// a separate useCurrentUser() instance in MobileFrame. After any QM write
// from this page, fire this event so MobileFrame refetches /api/me.
const ME_REFRESH_EVENT = "nymr-me-refresh";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DutyOfficer {
  id: number;
  first: string;
  last: string;
  /**
   * Mobile number for the call button and visible-text on the roster.
   * Emitted by /api/dashboard for today's rostered officers (canViewMobile
   * rule #5). Null when no phone is on file.
   */
  phone: string | null;
  /**
   * hub_users.id of the linked login (LEFT JOIN on email by the API).
   * Required for the Quick Message compose sheet (QM targets hub_users).
   * Null when the contact has no login — the QM button is suppressed.
   */
  hubUserId: number | null;
  /**
   * Phase 10 / Feature 6: surfaces `hub_users.is_first_aider` so the
   * roster panel can render a "First aider" stamp next to officers who
   * carry the flag. Defaults to false when the linked hub_users row is
   * missing.
   */
  isFirstAider: boolean;
  /**
   * Sign-In System (Build 1): true when this rostered officer currently has an
   * open duty clock-in session. Renders the green "Active and Signed-In for
   * Duty" tag next to their name on the roster.
   */
  signedInForDuty: boolean;
  /** migration 077 — EARLY (AM) / LATE (PM) for a Duty Fitter; absent/null otherwise. */
  shift?: "AM" | "PM" | null;
}

interface DutyToday {
  date: string;
  DM: DutyOfficer | null;
  MOM: DutyOfficer | null;
  EM: DutyOfficer | null;
  // Duty Controller — migration 078: up to two per day (EARLY/LATE), EARLY first.
  DC: DutyOfficer[];
  // Duty Fitter role — migration 077: up to two per day (EARLY/LATE), EARLY first.
  DF: DutyOfficer[];
  // migration 077 — common per-role duty phone, shown after each officer's own.
  rolePhones?: Record<string, string | null>;
}

interface AlertPayload {
  text: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

interface LatestEdition {
  id: number;
  issue_number: number;
  title: string;
  edition_date: string;
  editor_name: string;
  published_at: string | null;
}

interface FirstAiderOnDuty {
  hubUserId: number;
  first: string;
  last: string;
  phone: string | null;
  location: string | null;
}

interface DashboardResponse {
  alert: AlertPayload;
  dutyToday: DutyToday;
  latestEdition: LatestEdition | null;
  // Latest CEO newsletter the viewer is entitled to see (recipient-gated
  // server-side). Null when the viewer is on no published edition's
  // distribution. Same shape as the weekly edition.
  latestCeoEdition: LatestEdition | null;
  // Sign-In System + First Aider duty role — everyone signed in for duty AS a
  // First Aider, shown on the roster card.
  firstAidersOnDuty: FirstAiderOnDuty[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function HomePage() {
  const navigate = useNavigate();
  const { me, refresh: refreshMe } = useCurrentUser();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<DashboardResponse>("/api/dashboard")
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : "Couldn't load home.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onQmUnreadChanged = useCallback(() => {
    window.dispatchEvent(new Event(ME_REFRESH_EVENT));
  }, []);

  // Phase 9 follow-up -- Headlines is gated by a per-user app pref. Defaults
  // to true (DEFAULT_APPS server-side), so any user whose stored apps JSON
  // doesn't have the key still sees the section. The `!== false` form means
  // undefined ALSO shows it -- defensive against in-flight type/payload
  // mismatches across deploys.
  const showHeadlines = me?.apps?.headlines !== false;

  return (
    <div style={{ paddingBottom: 24 }}>
      <HomeHero me={me} />
      <div style={{ padding: "18px 16px 0", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Onboarding consents (migration 080) — access-critical orange banner.
            Renders only when the caller has unread onboarding documents; deep-
            links to the Confirmations & Consents app with the deadline countdown. */}
        <ConsentsBanner me={me} onOpen={() => navigate("/consents")} />
        {/* Password handover (escrow) — self-fetches /api/password/mine; renders
            null unless a password has been stored for this user AND the admin
            chose "show to user". Reveals once against the user's own login PIN. */}
        <PasswordReadyBanner />
        {/* Phase 8 — First Aider Callout. The prominent live-emergency card(s)
            take the top of Home for everyone involved (caller / responders /
            admins), above Quick Message. Self-renders null when nothing active. */}
        <ActiveCalloutCards />
        <QuickMessageHomeSection onUnreadChanged={onQmUnreadChanged} />
        {/* Sign-In System (Build 2) -- "Group Messages" strip: every group you're
            in + its active/unread state; tap a row to open its thread. Rendered
            OUTSIDE the dashboard-data gate (and not tied to duty/eligibility) so
            it stays visible the whole time you're logged in, regardless of the
            /api/dashboard fetch. */}
        <GroupQmSurface me={me} />
        {/* Events Live (volunteer loop) — call → pending/approved → live banner.
            Self-fetches the caller's open-call state; renders null when there's
            nothing relevant. Sits above the duty/roster furniture. */}
        <EventsLiveBanner onNavigate={(p) => navigate(p)} />
        {/* Newsletter Reader (migration 124) — latest published edition banner.
            Self-fetches /api/newsletter-reader/banner; renders null when nothing
            is live or the caller has dismissed it. Deep-links into the image reader. */}
        <NewsletterHomeCard
          onOpen={(id) => navigate(`/newsletter-reader/${id}`)}
          onOpenMobile={(id) => navigate(`/newsletter-reader/${id}/mobile`)}
          onAll={() => navigate("/newsletter-reader")}
        />
        {/* Mini-Blog (migration 128) — "new article" banner for the caller's
            department(s). Self-fetches /api/miniblog/banner; renders null when
            no visible article exists. Tapping opens the stream. */}
        <MiniBlogHomeBanner onOpen={() => navigate("/miniblog")} />
        {loading && <SkeletonStack />}
        {!loading && error && (
          <Card>
            <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>
              Couldn't load home.
            </div>
          </Card>
        )}
        {!loading && !error && data && (
          <>
            {data.alert.text && <AlertBanner text={data.alert.text} />}
            {showHeadlines && <HeadlinesSection />}
            <DutyCard
              duty={data.dutyToday}
              firstAiders={data.firstAidersOnDuty}
              onQmSent={onQmUnreadChanged}
            />
            {/* Sign-In System (Build 1) -- the duty button / sage status card.
                Self-gates to null for ineligible users, so Home is byte-identical
                to pre-feature for them (PHASE_8_ADDENDUM §9d silent gating). */}
            <DutySurface
              me={me}
              onChanged={() => {
                void refreshMe();
                window.dispatchEvent(new Event(ME_REFRESH_EVENT));
              }}
            />
            {(data.latestEdition || data.latestCeoEdition) && (
              <NewsletterStrip
                weekly={data.latestEdition}
                ceo={data.latestCeoEdition}
                onOpenWeekly={(id) => navigate(`/newsletter/${id}`)}
                onOpenCeo={(id) => navigate(`/newsletter/ceo/${id}`)}
              />
            )}
            {/* From Reports -- the pinned-measure slice, under all other Home
                content. Renders only for users who hold apps.reports and have
                pinned a measure (self-gating; otherwise null). */}
            <ReportsHomeSlice canSee={me?.apps?.reports === true} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewsletterHomeCard — Newsletter Reader (migration 124) latest-edition banner.
// Self-fetches the newest live edition the caller hasn't dismissed; renders null
// otherwise. Deep-links into the image reader. Dismissal is server-side (per
// edition) so it stays gone until the next edition and follows the user's devices.
// ---------------------------------------------------------------------------

interface NlBannerEdition {
  id: number;
  issueNo: number;
  title: string;
  blurb: string | null;
  coverUrl: string | null;
}

function NewsletterHomeCard({ onOpen, onOpenMobile, onAll }: { onOpen: (id: number) => void; onOpenMobile: (id: number) => void; onAll: () => void }) {
  const [edition, setEdition] = useState<NlBannerEdition | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ edition: NlBannerEdition | null }>("/api/newsletter-reader/banner")
      .then((d) => { if (!cancelled) setEdition(d.edition); })
      .catch(() => { /* home banner is best-effort; stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  if (!edition) return null;

  const dismiss = () => {
    const id = edition.id;
    setEdition(null); // optimistic
    void apiFetch(`/api/newsletter-reader/banner/${id}/dismiss`, { method: "POST", body: "{}" }).catch(() => {});
  };

  return (
    <Card>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {edition.coverUrl && (
          <img
            src={edition.coverUrl}
            alt={`${edition.title} cover`}
            style={{ width: 68, height: 92, objectFit: "cover", borderRadius: 6, flexShrink: 0, boxShadow: C.shadowS, background: C.paperWarm }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Stamp tone="brass">New newsletter</Stamp>
          <h3 style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 700, color: C.ink, margin: "6px 0 2px", letterSpacing: -0.2 }}>
            {edition.title}
          </h3>
          {edition.blurb && (
            <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, fontWeight: 500, marginBottom: 10 }}>
              {edition.blurb}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => onOpen(edition.id)}
              style={{ border: "none", background: C.brass, color: "#fff", fontFamily: F.body, fontWeight: 800, fontSize: 13, padding: "9px 16px", borderRadius: 10, cursor: "pointer" }}
            >
              View Desktop
            </button>
            <button
              onClick={() => onOpenMobile(edition.id)}
              style={{ border: "none", background: C.brassDk, color: "#fff", fontFamily: F.body, fontWeight: 800, fontSize: 13, padding: "9px 16px", borderRadius: 10, cursor: "pointer" }}
            >
              View Mobile
            </button>
            <button
              onClick={onAll}
              style={{ border: `1px solid ${C.hairlineLt}`, background: "transparent", color: C.inkSoft, fontFamily: F.body, fontWeight: 700, fontSize: 13, padding: "9px 14px", borderRadius: 10, cursor: "pointer" }}
            >
              Past editions
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          title="Dismiss"
          style={{ border: "none", background: "transparent", color: C.inkMute, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 4 }}
        >
          &times;
        </button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MiniBlogHomeBanner — Mini-Blog (migration 128) "new article" banner.
// Self-fetches the latest visible article for the caller's department(s);
// renders null when there is none (or the caller lacks the app / departments).
// Tapping the banner opens the continuous stream at /miniblog.
// ---------------------------------------------------------------------------

interface MiniBlogBannerArticle {
  id: number;
  department: string;
  title: string;
  authorName: string;
  publishedAt: string;
}

function MiniBlogHomeBanner({ onOpen }: { onOpen: () => void }) {
  const [article, setArticle] = useState<MiniBlogBannerArticle | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ article: MiniBlogBannerArticle | null }>("/api/miniblog/banner")
      .then((d) => { if (!cancelled) setArticle(d.article); })
      .catch(() => { /* home banner is best-effort; stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  if (!article) return null;

  const when = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(article.publishedAt));

  return (
    <Card>
      <button
        onClick={onOpen}
        aria-label={`Mini-Blog — new article: ${article.title}, by ${article.authorName}`}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Stamp tone="sage">Mini-Blog</Stamp>
          <Stamp tone="brass">New article</Stamp>
        </div>
        <h3 style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, margin: "8px 0 2px", letterSpacing: -0.2, overflowWrap: "anywhere" }}>
          {article.title}
        </h3>
        <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkMute, fontWeight: 600 }}>
          {article.authorName} · {when} · {article.department}
        </div>
      </button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PasswordReadyBanner — password handover (escrow). Self-fetches
// /api/password/mine; when a password is waiting AND the admin ticked "show to
// user", shows a brass attention banner. Tapping opens a modal that reveals the
// password once against the user's own InfoHub login PIN. Nothing is logged or
// persisted client-side — the revealed secret lives only in transient state and
// is dropped when the modal closes. After a reveal attempt the banner hides.
// ---------------------------------------------------------------------------

interface PasswordMine {
  available: boolean;
  expiresAt?: string;
}

function PasswordReadyBanner() {
  const [available, setAvailable] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PasswordMine>("/api/password/mine")
      .then((d) => {
        if (cancelled) return;
        setAvailable(d.available);
        setExpiresAt(d.expiresAt);
      })
      .catch(() => { /* home banner is best-effort; stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  if (!available) return null;

  const until = expiresAt
    ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(expiresAt))
    : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          background: `linear-gradient(160deg, ${C.brass}, ${C.brassDk})`,
          borderRadius: 14,
          padding: "13px 14px",
          color: C.inkOnDark,
          display: "flex",
          gap: 12,
          alignItems: "center",
          width: "100%",
          textAlign: "left",
          border: "none",
          cursor: "pointer",
          boxShadow: "0 6px 18px rgba(122,84,11,0.24), inset 0 1px 0 rgba(255,255,255,0.14)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: `repeating-linear-gradient(135deg, ${C.brassGlow} 0 6px, ${C.brassDk} 6px 12px)`,
          }}
        />
        <span style={{ display: "flex", width: 38, height: 38, borderRadius: 11, background: "rgba(255,255,255,0.18)", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.inkOnDark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 11h14v9H5z" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            <path d="M12 15v2" />
          </svg>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.body, fontWeight: 800, fontSize: 15 }}>Your NYMR password is ready to view</div>
          <div style={{ fontFamily: F.body, fontWeight: 600, fontSize: 12.5, opacity: 0.95, marginTop: 1, lineHeight: 1.4 }}>
            {until ? `Available until ${until} · you can view it once.` : "You can view it once — tap to reveal."}
          </div>
        </div>
        <span style={{ fontSize: 22, fontWeight: 700, opacity: 0.9, flexShrink: 0, lineHeight: 1 }} aria-hidden="true">→</span>
      </button>
      {open && (
        <PasswordRevealModal
          onClose={() => setOpen(false)}
          onConsumed={() => setAvailable(false)}
        />
      )}
    </>
  );
}

// Reveal modal for the home-page password banner. Asks for the user's normal
// InfoHub login PIN, then POSTs to /api/password/mine/reveal. On success the
// password is shown once with a copy button and the "erased in InfoHub" note.
// Any reveal attempt (success or failure) consumes the banner.
function PasswordRevealModal({ onClose, onConsumed }: { onClose: () => void; onConsumed: () => void }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const doReveal = async () => {
    if (!pin.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ password: string; erased: true }>("/api/password/mine/reveal", {
        method: "POST",
        body: JSON.stringify({ pin }),
      });
      setRevealed(res.password);
      setPin("");
      onConsumed(); // password is now spent server-side
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Could not reveal your password.");
      onConsumed(); // a failed attempt erases the stored password too
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — the user can select the text */ }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reveal your NYMR password"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(43,32,20,0.5)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18, zIndex: 240 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, background: C.card, borderRadius: 20, padding: "22px 20px 18px", border: `1px solid ${C.hairlineLt}`, boxShadow: "0 16px 48px rgba(0,0,0,0.32)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <span style={{ display: "flex", width: 40, height: 40, borderRadius: 11, background: C.brassBg, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.brassDk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 11h14v9H5z" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /><path d="M12 15v2" />
            </svg>
          </span>
          <h3 style={{ margin: 0, fontFamily: F.serif, fontSize: 21, fontWeight: 700, color: C.ink, letterSpacing: -0.3 }}>
            {revealed ? "Your password" : "Reveal your password"}
          </h3>
        </div>

        {revealed ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderRadius: 14, background: "#2B2014", border: `1px solid ${C.brassDk}`, marginBottom: 12 }}>
              <span style={{ flex: 1, minWidth: 0, fontFamily: F.mono, fontSize: 20, fontWeight: 700, letterSpacing: 1.5, color: C.brassGlow, wordBreak: "break-all", lineHeight: 1.2 }}>{revealed}</span>
              <button onClick={copy} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(244,226,168,0.14)", color: C.brassGlow, border: "1px solid rgba(244,226,168,0.28)", borderRadius: 10, padding: "8px 12px", fontFamily: F.body, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "11px 13px", borderRadius: 12, background: C.paperWarm, border: `1px solid ${C.hairline}`, marginBottom: 16 }}>
              <Icons.Info s={17} c={C.inkSoft} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 13, color: C.inkSoft, lineHeight: 1.45 }}>
                This password has now been erased in InfoHub. It does not affect the account in Active Directory.
              </div>
            </div>
            <Button variant="primary" fullWidth onClick={onClose}>Done</Button>
          </>
        ) : (
          <>
            <div style={{ fontFamily: F.body, fontWeight: 600, fontSize: 13.5, color: C.inkSoft, lineHeight: 1.5, marginBottom: 14 }}>
              Enter your normal InfoHub login PIN to view your password once.
            </div>
            <label style={{ display: "block", fontFamily: F.serif, fontStyle: "italic", fontSize: 13, fontWeight: 600, color: C.brassDk, marginBottom: 6, letterSpacing: 0.2 }}>Your InfoHub PIN</label>
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void doReveal(); }}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Your login PIN"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.hairline}`, background: C.card, fontFamily: F.mono, fontSize: 16, fontWeight: 700, letterSpacing: 3, color: C.ink, outline: "none", boxSizing: "border-box", marginBottom: 14 }}
            />
            {error && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "11px 13px", borderRadius: 12, background: C.signalBg, border: "1px solid rgba(184,58,43,0.22)", marginBottom: 14 }}>
                <Icons.Alert s={16} c={C.signalDk} style={{ marginTop: 1, flexShrink: 0 }} />
                <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 13, color: C.signalDk, lineHeight: 1.45 }}>{error}</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>{error ? "Close" : "Cancel"}</Button>
              {!error && (
                <Button variant="primary" onClick={doReveal} disabled={busy || !pin.trim()} style={{ flex: 1 }}>
                  {busy ? "Revealing…" : "Reveal"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HomeHero — cream-to-warm gradient + steam-loco decoration + greeting
// ---------------------------------------------------------------------------

function HomeHero({ me }: { me: MeResponse | null }) {
  const first = me?.first || me?.upn?.split("@")[0] || "friend";
  const greeting = timeOfDayGreeting();
  const today = formatLongDate(new Date());
  // Slice J: top-right decoration follows the user's logo preference.
  // Defaults to the Art Deco Train SVG (the existing visual).
  const logoPref = me?.preferences?.logo ?? "art-deco-train";

  return (
    <div
      style={{
        position: "relative",
        padding: "22px 20px 24px",
        background: `linear-gradient(165deg, ${C.brassSurf} 0%, ${C.paperSoft} 60%, ${C.paper} 100%)`,
        borderBottom: `1px solid ${C.hairlineLt}`,
        overflow: "hidden",
      }}
    >
      {logoPref === "nymr-logo" ? <NymrLogoDecor /> : <SteamLocoDecor />}
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: 6,
              background: C.sage,
              boxShadow: "0 0 8px rgba(110,140,114,0.5)",
            }}
            aria-hidden="true"
          />
          <Eyebrow>{today}</Eyebrow>
        </div>
        <h1
          style={{
            fontFamily: F.serif,
            fontSize: 32,
            fontWeight: 700,
            color: C.ink,
            margin: 0,
            letterSpacing: -0.5,
            lineHeight: 1.05,
          }}
        >
          Good {greeting},
          <br />
          <span style={{ color: C.brassDk }}>{first}.</span>
        </h1>
      </div>
    </div>
  );
}

// Slice J: NYMR website logo at the same dimensions as SteamLocoDecor.
// The asset itself lands in P2.6 at /logos/nymr-logo.svg.
function NymrLogoDecor() {
  return (
    <img
      src="/logos/nymr-logo.svg"
      alt=""
      aria-hidden="true"
      style={{
        position: "absolute",
        right: -20,
        top: 14,
        width: 220,
        height: 110,
        opacity: 0.32,
        objectFit: "contain",
        objectPosition: "right center",
        pointerEvents: "none",
      }}
    />
  );
}

function SteamLocoDecor() {
  return (
    <svg
      viewBox="0 0 240 120"
      style={{ position: "absolute", right: -20, top: 14, width: 220, height: 110, opacity: 0.14 }}
      aria-hidden="true"
    >
      <g fill="none" stroke={C.brassDk} strokeWidth="1.5">
        <circle cx="60" cy="40" r="14" />
        <circle cx="84" cy="28" r="18" />
        <circle cx="110" cy="34" r="14" />
        <circle cx="138" cy="22" r="22" />
        <circle cx="168" cy="38" r="16" />
      </g>
      <g fill={C.brassDk} fillOpacity="0.7">
        <rect x="22" y="76" width="180" height="22" rx="3" />
        <rect x="42" y="64" width="48" height="14" rx="2" />
        <rect x="98" y="60" width="34" height="18" rx="2" />
        <circle cx="58" cy="104" r="9" />
        <circle cx="100" cy="104" r="9" />
        <circle cx="158" cy="104" r="9" />
      </g>
      <line x1="0" y1="115" x2="240" y2="115" stroke={C.brassDk} strokeOpacity="0.5" strokeWidth="2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ConsentsBanner — onboarding consents nudge (migration 080). Orange, access-
// critical: shows when the caller has unread onboarding documents, with the
// rolling-deadline countdown, deep-linking to the Confirmations & Consents app.
// ---------------------------------------------------------------------------

function ConsentsBanner({ me, onOpen }: { me: MeResponse | null; onOpen: () => void }) {
  const o = me?.onboarding;
  if (!o || o.unreadCount <= 0) return null;
  const days = o.daysLeft;
  const overdue = days != null && days < 0;
  const sub =
    days == null
      ? "Tap to review and confirm."
      : overdue
        ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} — your sign-in may be restricted.`
        : `${days} day${days === 1 ? "" : "s"} left to confirm — tap to review.`;
  return (
    <button
      onClick={onOpen}
      style={{
        background: "linear-gradient(160deg, #E08A2B, #C2671C)",
        borderRadius: 14,
        padding: "13px 14px",
        color: C.inkOnDark,
        display: "flex",
        gap: 12,
        alignItems: "center",
        width: "100%",
        textAlign: "left",
        border: "none",
        cursor: "pointer",
        boxShadow: "0 6px 18px rgba(150,90,20,0.22), inset 0 1px 0 rgba(255,255,255,0.14)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: `repeating-linear-gradient(135deg, ${C.brassGlow} 0 6px, #C2671C 6px 12px)`,
        }}
      />
      <span style={{ display: "flex", width: 38, height: 38, borderRadius: 11, background: "rgba(255,255,255,0.18)", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 4 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.inkOnDark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.body, fontWeight: 800, fontSize: 15 }}>
          {o.unreadCount} document{o.unreadCount === 1 ? "" : "s"} to read &amp; confirm
        </div>
        <div style={{ fontFamily: F.body, fontWeight: 600, fontSize: 12.5, opacity: 0.95, marginTop: 1, lineHeight: 1.4 }}>{sub}</div>
      </div>
      <span style={{ fontSize: 22, fontWeight: 700, opacity: 0.9, flexShrink: 0, lineHeight: 1 }} aria-hidden="true">→</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// AlertBanner — signal-red gradient with diagonal-striped left edge
// ---------------------------------------------------------------------------

function AlertBanner({ text }: { text: string }) {
  return (
    <div
      style={{
        background: `linear-gradient(160deg, #D04A3A, ${C.signalDk})`,
        borderRadius: 14,
        padding: "13px 14px",
        color: C.inkOnDark,
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        boxShadow: "0 6px 18px rgba(138,42,31,0.22), inset 0 1px 0 rgba(255,255,255,0.12)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: `repeating-linear-gradient(135deg, ${C.brassGlow} 0 6px, ${C.signalDk} 6px 12px)`,
        }}
        aria-hidden="true"
      />
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          background: "rgba(255,255,255,0.13)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(255,255,255,0.18)",
          flexShrink: 0,
          marginLeft: 6,
        }}
      >
        <Icons.Alert s={16} c={C.brassGlow} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 11,
            color: "rgba(255,250,225,0.75)",
            fontWeight: 600,
            letterSpacing: 0.4,
            marginBottom: 2,
          }}
        >
          Notice from management
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{text}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DutyCard — today's MOM, DM, EM with phone-text + tap-to-call + Quick Msg.
// ---------------------------------------------------------------------------

// Phase 10 / Feature 1: DC added. The Avatar palette is brass | heather
// | sage | signal (signal stays reserved for First Aider) so DC reuses
// `heather` (same as DM). The "DC" stamp label disambiguates them
// visually — matches the DutyPage convention.
type DutyRoleKey = "MOM" | "DM" | "EM" | "DC" | "DF";
type DutyTone = "brass" | "heather" | "sage";

interface DutyRow {
  role: DutyRoleKey;
  label: string;
  tone: DutyTone;
  contact: DutyOfficer;
  shiftTag?: string;
  commonPhone?: string | null;
}

function shiftTagOf(shift: "AM" | "PM" | null | undefined): string | undefined {
  return shift === "AM" ? "EARLY (AM)" : shift === "PM" ? "LATE (PM)" : undefined;
}

function buildRoster(duty: DutyToday): DutyRow[] {
  const ordering: ReadonlyArray<{ role: DutyRoleKey; label: string; tone: DutyTone }> = [
    { role: "MOM", label: "Mobile Operations", tone: "brass" },
    { role: "DM",  label: "Duty Manager",      tone: "heather" },
    { role: "EM",  label: "Event Manager",     tone: "sage" },
    { role: "DC",  label: "Duty Controller",   tone: "heather" },
    { role: "DF",  label: "Duty Fitter",       tone: "heather" },
  ];
  const rolePhones = duty.rolePhones ?? {};
  const rows: DutyRow[] = [];
  for (const entry of ordering) {
    const commonPhone = rolePhones[entry.role] ?? null;
    if (entry.role === "DF" || entry.role === "DC") {
      const list = entry.role === "DF" ? duty.DF : duty.DC;
      for (const c of list) {
        rows.push({ role: entry.role, label: entry.label, tone: entry.tone, contact: c, shiftTag: shiftTagOf(c.shift), commonPhone });
      }
    } else {
      const c = duty[entry.role];
      if (c) rows.push({ role: entry.role, label: entry.label, tone: entry.tone, contact: c, commonPhone });
    }
  }
  return rows;
}

interface QmTarget {
  hubUserId: number;
  firstName: string;
  lastName: string;
}

function DutyCard({
  duty,
  firstAiders,
  onQmSent,
}: {
  duty: DutyToday;
  firstAiders: FirstAiderOnDuty[];
  onQmSent: () => void;
}) {
  const roster = buildRoster(duty);
  const stampDate = formatDutyDate(duty.date);
  const [qmTarget, setQmTarget] = useState<QmTarget | null>(null);
  const [sentToFirstName, setSentToFirstName] = useState<string | null>(null);

  // Auto-dismiss the "Sent to ..." toast after a few seconds so it doesn't
  // hang around if the user takes a long look at the page.
  useEffect(() => {
    if (sentToFirstName === null) return;
    const t = window.setTimeout(() => setSentToFirstName(null), 3200);
    return () => window.clearTimeout(t);
  }, [sentToFirstName]);

  return (
    <>
      <Card padded={false}>
        <div
          style={{
            padding: "14px 16px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Eyebrow>On duty today</Eyebrow>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 20,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: -0.2,
                marginTop: 2,
              }}
            >
              The roster
            </div>
          </div>
          <Stamp tone="brass">{stampDate}</Stamp>
        </div>
        <DoubleRule style={{ margin: "0 16px" }} />
        <div style={{ padding: "4px 16px 14px" }}>
          {roster.length === 0 && firstAiders.length === 0 && (
            <div
              style={{
                padding: "16px 0",
                textAlign: "center",
                fontFamily: F.serif,
                fontStyle: "italic",
                color: C.inkMute,
              }}
            >
              No duty assignments for today
            </div>
          )}
          {roster.map((r, i) => (
            <div
              key={`${r.role}-${r.contact.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 0",
                borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}`,
              }}
            >
              <Avatar first={r.contact.first} last={r.contact.last} size={48} tone={r.tone} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Stamp tone={r.tone}>{r.role}</Stamp>
                  <span
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      fontSize: 11,
                      color: C.inkMute,
                      fontWeight: 600,
                    }}
                  >
                    {r.label}
                  </span>
                  {r.shiftTag && <Stamp tone="brass">{r.shiftTag}</Stamp>}
                  {/* Phase 10 / Feature 6: surface First Aider on the
                      roster, matching the stamp used on AccessUsersPage. */}
                  {r.contact.isFirstAider && (
                    <Stamp tone="signal">First aider</Stamp>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontSize: 18,
                      fontWeight: 700,
                      color: C.ink,
                      lineHeight: 1.1,
                      letterSpacing: -0.2,
                    }}
                  >
                    {r.contact.first} {r.contact.last}
                  </div>
                  {/* Sign-In System (Build 1): green tag when this rostered
                      officer is currently clocked in for duty. */}
                  {r.contact.signedInForDuty && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 9px",
                        borderRadius: 999,
                        background: C.sageBg,
                        border: `1px solid ${C.sageLt}`,
                        fontFamily: F.body,
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: C.sageDk,
                        letterSpacing: 0.2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{ width: 6, height: 6, borderRadius: "50%", background: C.sage, flexShrink: 0 }}
                      />
                      Signed-In
                    </span>
                  )}
                </div>
                {r.contact.phone && (
                  <a
                    href={`tel:${r.contact.phone}`}
                    style={{
                      display: "inline-block",
                      marginTop: 4,
                      fontFamily: F.mono,
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.brassDk,
                      letterSpacing: 0.4,
                      textDecoration: "none",
                    }}
                  >
                    {r.contact.phone}
                  </a>
                )}
                {r.commonPhone && (
                  <a
                    href={`tel:${r.commonPhone}`}
                    style={{
                      display: "block",
                      marginTop: 2,
                      fontFamily: F.mono,
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: C.inkSoft,
                      letterSpacing: 0.3,
                      textDecoration: "none",
                    }}
                  >
                    Common Duty Phone: {r.commonPhone}
                  </a>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {r.contact.hubUserId !== null && (
                  <ContactQmButton
                    onClick={() =>
                      setQmTarget({
                        hubUserId: r.contact.hubUserId as number,
                        firstName: r.contact.first,
                        lastName: r.contact.last,
                      })
                    }
                    ariaLabel={`Send Quick Message to ${r.contact.first} ${r.contact.last}`}
                  />
                )}
                {r.contact.phone && (
                  <a
                    href={`tel:${r.contact.phone}`}
                    aria-label={`Call ${r.contact.first} ${r.contact.last}`}
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 11,
                      background: C.brassBg,
                      border: `1px solid ${C.brassLt}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textDecoration: "none",
                      color: C.brassDk,
                    }}
                  >
                    <Icons.Phone s={16} c={C.brassDk} />
                  </a>
                )}
              </div>
            </div>
          ))}

          {/* First aiders signed in for duty (role_code = firstaider). Added to
              the roster card so on-duty first aiders are visible alongside the
              DM/MOM/EM/DC rota. */}
          {firstAiders.length > 0 && (
            <div
              style={{
                marginTop: roster.length > 0 ? 6 : 0,
                paddingTop: roster.length > 0 ? 12 : 4,
                borderTop: roster.length > 0 ? `1px solid ${C.hairlineLt}` : "none",
              }}
            >
              <Eyebrow>First aiders on duty</Eyebrow>
              {firstAiders.map((fa) => (
                <div key={fa.hubUserId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
                  <Avatar first={fa.first} last={fa.last} size={44} tone="signal" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                      <Stamp tone="signal">First aider</Stamp>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "3px 9px",
                          borderRadius: 999,
                          background: C.sageBg,
                          border: `1px solid ${C.sageLt}`,
                          fontFamily: F.body,
                          fontSize: 10.5,
                          fontWeight: 800,
                          color: C.sageDk,
                          letterSpacing: 0.2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: C.sage }} />
                        Signed-In
                      </span>
                      {fa.location && (
                        <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11, color: C.inkMute, fontWeight: 600 }}>
                          {fa.location}
                        </span>
                      )}
                    </div>
                    <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.1, letterSpacing: -0.2 }}>
                      {fa.first} {fa.last}
                    </div>
                    {fa.phone && (
                      <a
                        href={`tel:${fa.phone}`}
                        style={{ display: "inline-block", marginTop: 4, fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.brassDk, letterSpacing: 0.4, textDecoration: "none" }}
                      >
                        {fa.phone}
                      </a>
                    )}
                  </div>
                  {fa.phone && (
                    <a
                      href={`tel:${fa.phone}`}
                      aria-label={`Call ${fa.first} ${fa.last}`}
                      style={{ width: 38, height: 38, borderRadius: 11, background: C.brassBg, border: `1px solid ${C.brassLt}`, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", color: C.brassDk }}
                    >
                      <Icons.Phone s={16} c={C.brassDk} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {qmTarget && (
        <ComposeSheet
          open={true}
          recipient={qmTarget}
          onCancel={() => setQmTarget(null)}
          onSent={(info) => {
            setQmTarget(null);
            setSentToFirstName(info.firstName);
            onQmSent();
          }}
        />
      )}

      {sentToFirstName && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "calc(72px + env(safe-area-inset-bottom))",
            background: C.ink,
            color: "#FFFCEC",
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13.5,
            padding: "10px 16px",
            borderRadius: 999,
            boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
            zIndex: 250,
            letterSpacing: 0.2,
          }}
        >
          Quick message sent to {sentToFirstName}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// NewsletterStrip — compact "latest editions" block. The weekly and (when the
// server includes it — recipient-gated) CEO newsletter each render as a short
// single-bar card: a rectangular detail pill (edition · name · date) sitting
// inside a brand-colour blob. Two of these occupy roughly the height the one
// tall teaser used to.
// ---------------------------------------------------------------------------

function NewsletterStrip({
  weekly,
  ceo,
  onOpenWeekly,
  onOpenCeo,
}: {
  weekly: LatestEdition | null;
  ceo: LatestEdition | null;
  onOpenWeekly: (id: number) => void;
  onOpenCeo: (id: number) => void;
}) {
  return (
    <div>
      <Section title="Latest newsletters" />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {weekly && (
          <NewsletterBar
            tone="weekly"
            kicker="This week's update"
            edition={weekly}
            onOpen={() => onOpenWeekly(weekly.id)}
          />
        )}
        {ceo && (
          <NewsletterBar
            tone="ceo"
            kicker="CEO briefing"
            edition={ceo}
            onOpen={() => onOpenCeo(ceo.id)}
          />
        )}
      </div>
    </div>
  );
}

const NEWSLETTER_TONES = {
  weekly: { base: C.sage, dk: C.sageDk, lt: C.sageLt, bg: C.sageBg },
  ceo: { base: C.heather, dk: C.heatherDk, lt: C.heatherLt, bg: C.heatherBg },
} as const;

function NewsletterBar({
  tone,
  kicker,
  edition,
  onOpen,
}: {
  tone: "weekly" | "ceo";
  kicker: string;
  edition: LatestEdition;
  onOpen: () => void;
}) {
  const p = NEWSLETTER_TONES[tone];
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        border: "none",
        padding: 0,
        background: "none",
        cursor: "pointer",
      }}
    >
      {/* colour blob */}
      <div
        style={{
          background: `linear-gradient(135deg, ${p.base}, ${p.dk})`,
          borderRadius: 14,
          padding: "8px 8px 9px",
          boxShadow: C.shadowM,
        }}
      >
        {/* kicker row — newsletter name + read affordance, light-on-colour */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1px 6px 6px",
          }}
        >
          <span
            style={{
              fontFamily: F.body,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.92)",
            }}
          >
            {kicker}
          </span>
          <span
            style={{
              fontFamily: F.body,
              fontSize: 11.5,
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: 0.2,
            }}
          >
            Read ›
          </span>
        </div>

        {/* the rectangular detail pill — edition · name · date in one bar */}
        <div
          style={{
            background: C.paperSoft,
            borderRadius: 10,
            padding: "9px 11px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
          }}
        >
          <span
            style={{
              flexShrink: 0,
              fontFamily: F.mono,
              fontSize: 11,
              fontWeight: 700,
              color: p.dk,
              background: p.bg,
              border: `1px solid ${p.lt}`,
              borderRadius: 7,
              padding: "3px 7px",
              letterSpacing: 0.3,
            }}
          >
            No.{edition.issue_number}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: F.serif,
              fontSize: 15,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {edition.title}
          </span>
          <span
            style={{
              flexShrink: 0,
              fontFamily: F.mono,
              fontSize: 10.5,
              fontWeight: 700,
              color: C.inkMute,
              letterSpacing: 0.3,
              whiteSpace: "nowrap",
            }}
          >
            {formatShortIsoDate(edition.edition_date)}
          </span>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SkeletonStack — three paperWarm placeholders while data is in flight
// ---------------------------------------------------------------------------

function SkeletonStack() {
  return (
    <>
      <SkeletonRow h={68} />
      <SkeletonRow h={260} />
      <SkeletonRow h={120} />
    </>
  );
}

function SkeletonRow({ h }: { h: number }) {
  return (
    <div
      style={{
        height: h,
        background: C.paperWarm,
        borderRadius: 18,
        border: `1px solid ${C.hairlineLt}`,
      }}
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function timeOfDayGreeting(): "morning" | "afternoon" | "evening" {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function formatLongDate(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

function formatShortIsoDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatDutyDate(iso: string): string {
  // "2026-05-14" -> "14-05-26"
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y.slice(2)}`;
}
